import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { IClientInfoRequest, mapAlignmentToContentPosition } from './model/client-info.interface';
import { ISDKConfig } from './model/sdk-config.interface';
import { CidaasService } from './service/cidaas.service';
import { Color, SharedDataService } from './service/shared-data.service';
import { CidaasHostedPageColorService } from 'cidaas-hosted-page-toolkit';
import { GetTranslationService, TranslationDocCollection } from './service/get-translation.service';
import { distinctUntilChanged, filter, map, mergeAll, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { from, Observable, of, Subject } from 'rxjs';
import { TenantInfoApi } from './model/tenant-info.interface';
import { ContentPosition } from './model/ui-view.model';
import { lastTwoValues } from './helpers/rx-ops.helper';
import { LocaleService } from './service/locale.service';
import { ColorCalculateService } from './service/color-calculate.service';
import { skipErrorCheck, PathsToSkip } from './app.model';
import { addBundledLanguages } from './model/localization.model';

const UILocaleQueryParams = 'ui_locales';
const BackgroundColor = '#ffffff';

/**
 * AppTranslations configured for the application.
 * Right now includes bundled translation + custom translation
 * @property {AppTranslation} doc translation for app as array [Bundled, CustomTranslation]
 * @property {string} lang language/locale for the translation loaded
 * */
export interface AppTranslation {
  doc: TranslationDocCollection;
  lang: string;
}

interface QueryParams {
  requestId: string;
  error: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  // variable declaration
  title = 'default-hosted-pages-webapp';
  logoImage = '';
  favIcon = ''
  requestId = '';
  primaryColor = '';
  secondaryColor = '';
  algorithm = '';
  primaryColorPalette;
  secondaryColorPalette;
  palette: any[] = [];
  cssString = document.querySelector('.dynamicColor')?.innerHTML;
  contentAlign = 'CENTER'; // default is center
  bgUri = '';
  mediaType = 'IMAGE'; // default is image
  videoUrl = '';
  logoPosition: ContentPosition = ContentPosition.Center;

  unsubscribe$ = new Subject();
  pageLoading: boolean = false;
  isAppUser: boolean = false;

  colorPickerText = BackgroundColor;

  // constructor
  constructor(
    private dataService: SharedDataService,
    private cidaasSDK: CidaasService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private colorService: CidaasHostedPageColorService,
    private translationService: GetTranslationService,
    private localeService: LocaleService,
    private colorCalculateService: ColorCalculateService,
    private _router: Router
  ) { }

  // OnInit lifecycle method
  ngOnInit(): void {
    // Add 'implements OnInit' to the class.
    // this.bgImage = '';
    this.init();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  // Component property methods
  init(): void {
    // getting base url
    this.dataService._setBaseURL(environment.base_url);
    this.setLanguage();
    this.dataService.currentAlgorithm.subscribe((algorithm) => {
      this.algorithm = algorithm;
    });

    this.dataService.currentPrimaryColor.subscribe((primaryColor) => {
      this.primaryColor = this.colorCalculateService._getCalculateColor(BackgroundColor, primaryColor);
      this.primaryColorPalette = this.colorService.computeColors(primaryColor, 'primary', this.algorithm);
      this.setColors(this.primaryColorPalette);
    });

    this.dataService.currentSecondaryColor.subscribe((secondaryColor) => {
      this.secondaryColor = secondaryColor;
      this.secondaryColorPalette = this.colorService.computeColors(secondaryColor, 'secondary', this.algorithm);
      this.setColors(this.secondaryColorPalette);
      this.setIeStyle();
    });
    // intialising cidaas sdk
    this.initCidaasSDK({ authority: environment.base_url });

    // getting requestId
    const params$: Observable<QueryParams> = this.route.queryParams.pipe(
      // Map params to requestId and error
      map(params => ({ requestId: params.requestId, error: params.error })),
      // Only emit query params if any requestId or error params has changed
      distinctUntilChanged(
        (prev, curr) => prev.requestId === curr.requestId || prev.error === curr.error
      )
    );

    // Router end event
    const routerEnd$ = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        switchMap(() => params$)
      );

      // acid identify App Origin
      this.route.queryParams.subscribe(params => {
          if(params['user_origin'] === 'app' || localStorage.getItem("user_origin") === "app") {
              let user_origin_storage = localStorage.getItem("user_origin");
              if(user_origin_storage === undefined || user_origin_storage === null) {
                  localStorage.setItem("user_origin", "app");
              }
              this.isAppUser = true;
              this.dataService._setIsAppUser(true);
          }
      });

    from([this.getTranslationWithLocale$(), routerEnd$])
      .pipe(
        tap(() => this.pageLoading = true),
        mergeAll(2),
        takeUntil(this.unsubscribe$),
      ).subscribe((value) => {
        if (this.isTranslationDoc(value)) {
          this.setupLanguage(value.lang, value.doc);
          this.pageLoading = false;
        }
        else {
          // If requestId is null/empty redirect to error page
          if (!value.requestId) {
            const errorRoutes = PathsToSkip;
            const path = this.getPathName().split('/')[2];
            const isDevicePage = skipErrorCheck(path);

            if (!errorRoutes.includes(path) && !isDevicePage){
              const errorCode = value && value.error && value.error.toUpperCase() || 'LINK_EXPIRED_OR_INVALID';
              const queryParams: Params = { error_code: errorCode, error_hint: 'LINK_EXPIRED_OR_INVALID'};
              this.cidaasSDK.navigateToErrorPage(queryParams);
            }
            // Call theme set if device page success
            if (isDevicePage) {
              this.setDynamicComponents({});
            }
            // We return so that we don't make a public call with empty requestId
            return;
          }
          const requestId = value.requestId;
          this.requestId = requestId;
          this.dataService._setRequestId(requestId);
          this.getClientInfo({ requestId });
        }
        this.setDynamicComponents({ requestId: this.requestId });
    }, () => this.pageLoading = false);

    // get client info
    // get logo url
    this.cidaasSDK._getDeviceInfo();
  }

  getPathName() {
    return window.location.pathname;
  }

  setupLanguage(language: string, docCollection: TranslationDocCollection): void {
    const [defaultTranslation, tenantTranslation] = docCollection;
    if (defaultTranslation) {
      this.translate.setTranslation(language, defaultTranslation);
    }
    // merging teanant specific translation to the original translation
    if (tenantTranslation) {
      this.translate.setTranslation(language, tenantTranslation, true);
    }
    this.translate.use(language);
    this.dataService._setTranslationLoadStatus(true);
  }

  getUILocaleParam$(): Observable<string | undefined> {
    return this.route.queryParams.pipe(
      map(param => param[UILocaleQueryParams] as string),
      lastTwoValues(),
      switchMap(value => {
        // If the previous route had uiLocales defined and next route doesn't
        if (value.previous && !value.current) {
          const locale = value.previous;
          // reload the next route with missing uiLocales
          return this.forceUILocales$(value.previous);
        }
        return of(value.current);
      }),
      distinctUntilChanged()
    );
  }

  forceUILocales$(locale: string): Observable<string> {
    return from(this.router.navigate([], {
      queryParams: { [UILocaleQueryParams]: locale },
      queryParamsHandling: 'merge'
    })).pipe(
      map(() => locale)
    );
  }

  isTranslationDoc(value: QueryParams | AppTranslation): value is AppTranslation {
    return (value as AppTranslation).doc !== undefined;
  }

  getTranslationWithLocale$ = (): Observable<AppTranslation> => {
    return this.getUILocaleParam$().pipe(
      switchMap(this.getTranslation$)
    );
  }

  getTranslation$ = (localeToLoad?: string): Observable<AppTranslation> => {
    return this.getTenantInfo$().pipe(
      map(tenantInfo => tenantInfo.tenant_key),
      switchMap((tenantKey) => this.getTranslationForTenant$(tenantKey, localeToLoad))
    );
  }

  getTenantInfo$(): Observable<TenantInfoApi> {
    return this.cidaasSDK
      .getTenantInfo()
      .pipe(
        distinctUntilChanged((prevTenant, currentTenant) =>
          prevTenant.tenant_key !== currentTenant.tenant_key),
        take(1)
      );
  }

  getTranslationForTenant$ = (tenantKey: string, localeToLoad?: string): Observable<AppTranslation> => {
    return this.getTranslationDoc$(tenantKey, localeToLoad);
  }

  // initializing cidaas sdk
  initCidaasSDK(options: Pick<ISDKConfig, 'authority'>): void {
    this.cidaasSDK._initSDK(options);
  }

  // get client info
  getClientInfo(options: IClientInfoRequest): void {
    this.cidaasSDK._getClientInfo(options);
  }

  // get logo url
  setDynamicComponents(options: { requestId?: string }): void {
    this.cidaasSDK._getClientInfoWithTenant(options).subscribe(([clientInfoData, tenantInfoData]) => {
      if (clientInfoData?.logo_uri || tenantInfoData && tenantInfoData?.logo_uri) {
        this.logoImage = clientInfoData?.logo_uri || tenantInfoData?.logo_uri || ''  ;
        this._setFavicon(this.logoImage);
      }

      if (clientInfoData?.fav_icon_uri || tenantInfoData && tenantInfoData?.fav_icon_uri) {
        this.favIcon = clientInfoData?.fav_icon_uri || tenantInfoData?.fav_icon_uri || '';
        this._setFavicon(this.favIcon);
      }

      if (clientInfoData?.accentColor || tenantInfoData?.accentColor) {
        const data = clientInfoData?.accentColor || tenantInfoData?.accentColor;
        this.dataService.changeSecondaryColor(data || '#ef4923');
      }
      if (clientInfoData?.primaryColor || tenantInfoData?.primaryColor) {
        const data = clientInfoData?.primaryColor || tenantInfoData?.primaryColor;
        this.dataService.changePrimaryColor(data || '#f7941d');
      }
      if (clientInfoData?.contentAlign || tenantInfoData?.contentAlign) {
        this.contentAlign = clientInfoData?.contentAlign || tenantInfoData?.contentAlign || 'CENTER';
      }

      if (clientInfoData?.logoAlign || tenantInfoData?.logoAlign) {
        const data = clientInfoData?.logoAlign || tenantInfoData?.logoAlign || '';
        this.logoPosition = mapAlignmentToContentPosition(data)
          || ContentPosition.Center;
      }
      if (clientInfoData?.mediaType || tenantInfoData?.mediaType) {
        this.mediaType = clientInfoData?.mediaType || tenantInfoData?.mediaType || 'IMAGE';
      }
      if (
        (clientInfoData?.mediaType === 'IMAGE' && clientInfoData?.backgroundUri)
        || (tenantInfoData?.mediaType === 'IMAGE' && tenantInfoData?.backgroundUri)
      ) {
        const backgroundUri = clientInfoData?.backgroundUri || tenantInfoData?.backgroundUri || '';
        if (backgroundUri) {
          document.documentElement.style.setProperty(`--background-image`, `url(${backgroundUri})`);
          this.bgUri = backgroundUri;
          document.documentElement.style.setProperty(`--cidaas-default-bg-color`, 'transparent');
        }
      }
      else if (
        (clientInfoData?.mediaType === 'VIDEO' && clientInfoData.videoUrl)
        || (tenantInfoData?.mediaType === 'VIDEO' && tenantInfoData.videoUrl)
      ) {
        this.videoUrl = clientInfoData?.videoUrl || tenantInfoData?.videoUrl || '';
        document.documentElement.style.setProperty(`--cidaas-default-bg-color`, 'transparent');
      } else {
        document.documentElement.style.setProperty(`--cidaas-default-bg-color`, '#f7f8fd');
      }
    });
  }

  setColors(palette: Color[]): void {
    for (const color of palette) {
      if (color.name === '--theme-primary-500') {
        document.documentElement.style.setProperty('--primary-color', color.hex);
      }
      if (color.name === '--theme-secondary-500') {
        document.documentElement.style.setProperty('--secondary-color', color.hex);
      }
      document.documentElement.style.setProperty(color.name, color.hex);
    }
  }
  setIeStyle = () => {
    document.querySelectorAll('#dynamicColor').forEach((e) => e.parentNode?.removeChild(e));
    if (
      navigator.appVersion.toUpperCase().indexOf('MSIE') !== -1 ||
      navigator.appVersion.toUpperCase().indexOf('TRIDENT') !== -1 ||
      navigator.appVersion.toUpperCase().indexOf('EDGE') !== -1
    ) {
      const palette: any = [];
      this.primaryColorPalette.map((x) => {
        palette.push(x);
      });
      this.secondaryColorPalette.map((x) => {
        palette.push(x);
      });
      palette.forEach((x) => {
        if (x.name === '--theme-primary-500') {
          this.cssString = this.cssString?.split('var(--primary-color)').join(x.hex);
        }
        if (x.name === '--theme-secondary-500') {
          this.cssString = this.cssString?.split('var(--secondary-color)').join(x.hex);
        }
        this.cssString = this.cssString?.split('var(' + x.name + ')').join(x.hex);
      });
      // creating the style tag
      const head = document.head || document.getElementsByTagName('head')[0];
      const style = document.createElement('style');
      style.type = 'text/css';
      style.id = 'dynamicColor';
      if (style['styleSheet']) {
        // This is required for IE8 and below.
        style['styleSheet']['cssText'] = this.cssString;
      } else {
        style.appendChild(document.createTextNode(this.cssString || ''));
      }
      head.appendChild(style);
      document.querySelectorAll('.dynamicColor').forEach((e) => e.parentNode?.removeChild(e));
    } else {
      document.querySelectorAll('.dynamicColor').forEach((e) => e.parentNode?.removeChild(e));
    }
  }

  setLanguage(): void {
    this.translate.setDefaultLang('en');
  }


  /**
   * getTranslationDoc$ will return app translation docs configured for the tenant by config.json
   * checking if localeToLoad is supported.
   * Note: if no locale is provided the app will try to load bundled locale, with en as fallback.
   * @param {string} tenantKey tenantKey of the customer
   * @param {string} localeToLoad optional locale to force load translation for given locale. If none is given
   * browser lanugage takes preference.
   * @returns {Observable<AppTranslation>} app translation bundle for given locale, or
   * **/
  getTranslationDoc$ = (tenantKey: string, localeToLoad?: string): Observable<AppTranslation> => {
    return this.translationService.getTenantTranslationConfig(tenantKey)
      .pipe(
        map(({ locale: customLocales }) => {
          const supportedLang = this.setLanguageToTranslationService(customLocales);
          // Check if localeTo load is supported by custom locales
          const lang = this.localeService.getLocaleToLoad(localeToLoad, supportedLang);
          // Returns loadCustom true, if locale to load is found in custom locales
          return {
            lang: lang,
            loadCustom: customLocales.includes(lang)
          }
        }),
        switchMap(({lang, loadCustom}) =>
          this.translationService.getTranslationStrings(lang, tenantKey, loadCustom).pipe(
            map(doc => ({ doc, lang }))
          )
        )
      );
  }

  // TODO: Move all of it into a localization service
  setLanguageToTranslationService = (supportedLanguage: string[]): string[] => {
    this.translate.addLangs(supportedLanguage);
    const languages = this.translate.getLangs();

    return addBundledLanguages(languages);
  }

    backClick(error: string = '') {

        let params = new URLSearchParams(this._router.url);
        let gloTypeRegister = params.get('gloTypeRegister');
        if (gloTypeRegister === null) {
            window.location.replace( 'https://www.globus-baumarkt.de/');
        }

        this._router.navigate(['/login'], {
            queryParams: {
                requestId: this.requestId,
                ...( error && { error } || {})
            },
        });
    }

  _setFavicon = (logoURL: string) => {
    document.querySelector('#appFavicon')?.setAttribute('href', logoURL);
  }
}
